import type { VueFinalModalProperty } from 'vue-final-modal';

import AlertModal from '@/components/modals/AlertModal.vue';

import { getVm } from '@/helpers/vm-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { ModalHelper } from '@/helpers/modal-helper-vfm';

export interface AlertButton {
	text: string;
	role?: 'cancel' | 'destructive' | string;
	cssClass?: string | string[];
	handler?: (value?: any) =>
		| boolean
		| void
		| {
				[key: string]: any;
		  };
}

export interface AlertInput {
	type?:
		| 'date'
		| 'email'
		| 'number'
		| 'password'
		| 'search'
		| 'tel'
		| 'text'
		| 'url'
		| 'time'
		| 'week'
		| 'month'
		| 'datetime-local'
		| 'checkbox'
		| 'radio'
		| 'textarea';
	name?: string;
	placeholder?: string;
	value?: any;
	label?: string;
	checked?: boolean;
	disabled?: boolean;
	id?: string;
	handler?: (input: AlertInput) => void;
	min?: string | number;
	max?: string | number;
	cssClass?: string | string[];
	attributes?: any;
	tabindex?: number;
}

export interface ShowAlertPayload {
	header: string;
	subHeader?: string;
	message?: string;
	buttons: string[];
	options?: Object;
	additionalContexts?: SnowplowContext[];
}

export interface ShowPromptPayload {
	header: string;
	subHeader?: string;
	message?: string;
	buttons: AlertButton[];
	options?: Object;
	inputs?: AlertInput[];
}

function getVfm() {
	return getVm().$vfm as VueFinalModalProperty | null;
}

const showAlert = (payload: ShowAlertPayload) => {
	const { additionalContexts = [] } = payload;
	return new Promise(async (resolve, reject) => {
		await ModalHelper.ensureVfmCss();

		// return the index of the clicked button or null if the alert has been cancelled
		let clickResult: number | null = null;
		const { header, subHeader = '', message = '', buttons, options = {} } = payload;

		const $vfm = getVfm();
		if (!$vfm) {
			reject('VFM not available');
			throw new Error('VFM not available');
		}

		const alertId = `alert-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

		$vfm.show({
			component: 'VueFinalModal',
			bind: {
				name: alertId,
				escToClose: true,
			},
			on: {
				opened: () => {
					TrackingHelper.trackEvent(
						'userinteraction',
						{
							action: 'alert_opened',
							nonInteraction: true,
						},
						additionalContexts
					);
				},
				closed: () => {
					TrackingHelper.trackEvent(
						'userinteraction',
						{
							action: 'alert_closed',
							label: clickResult + '',
						},
						additionalContexts
					);
				},
			},
			slots: {
				default: {
					component: AlertModal,
					bind: {
						header,
						subHeader,
						message,
						buttons: buttons.map((button, index) => {
							return {
								text: button,
								cssClass: `alert-message__button-${index}`,
								handler: () => {
									clickResult = index;
									resolve(index);
									$vfm.hide(alertId);
								},
							};
						}),
						...options,
					},
				},
			},
		});
	});
};

const promptAlert = (payload: ShowPromptPayload) =>
	new Promise(async (resolve, reject) => {
		await ModalHelper.ensureVfmCss();

		let clickResult: number | null = null;
		const { header, subHeader = '', inputs, message = '', buttons, options = {} } = payload;

		const $vfm = getVfm();
		if (!$vfm) {
			reject('VFM not available');
			throw new Error('VFM not available');
		}

		const alertId = `alert-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

		$vfm.show({
			component: 'VueFinalModal',
			bind: {
				name: alertId,
				escToClose: true,
			},
			on: {
				opened: () => {
					TrackingHelper.trackEvent('userinteraction', {
						action: 'alert_opened',
						nonInteraction: true,
					});
				},
				closed: () => {
					TrackingHelper.trackEvent('userinteraction', {
						action: 'alert_closed',
						label: clickResult + '',
					});
				},
			},
			slots: {
				default: {
					component: AlertModal,
					bind: {
						header,
						subHeader,
						inputs,
						message,
						buttons: buttons.map((button, index) => {
							return {
								...button,
								cssClass: `alert-message__button-${index}`,
								handler: (data: any) => {
									clickResult = index;
									if (button.handler) {
										let hasNotFilledAllInputs = false;
										// Check if user has filled all inputs, and if not, don't remove the prompt.
										Object.values(data).forEach(key => {
											if (key === '') {
												hasNotFilledAllInputs = true;
											}
										});
										if (!hasNotFilledAllInputs) {
											button?.handler(data);
											resolve(index);
											$vfm.hide(alertId);
										}
									} else {
										resolve(index);
										$vfm.hide(alertId);
									}
								},
							};
						}),
						...options,
					},
				},
			},
		});
	});

export { promptAlert, showAlert };
